import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { commafy } from "../../utils/numberFormat";
import useAuth from "../../contexts/authcontext";
import { Swiper, SwiperSlide } from "swiper/react";
import { TAB_KEY_NAGHDI } from "utils/constants";
import { UserCardContainer } from "./style/userCard.style";

export const UserCard = ({
  qr,
  setContract,
  qrContracts,
  setActiveSlideHandler,
  showWallet = false,
  isCash,
  setSwiper,
  sortedCardsIndexHandler,
  qrFormData,
}) => {
  const { user } = useAuth();
  const router = useRouter();
  const swiperRef = useRef();

  const [userContracts, setUserContracts] = useState([]);
  const [noRightArrow, setNoRightArrow] = useState(true);

  // Sort list by credit limit remaining (descending)
  const sortByCreditRemain = (list) =>
    list.sort((a, b) => +b.credit_limit_remain - +a.credit_limit_remain);

  // Sort contracts based on various criteria
  const sortContracts = (contracts) => {
    const nasibaGuarantors = contracts.filter((contract) =>
      contract.wallet ? true : contract.guarantor?.id === 1,
    );
    const otherGuarantors = contracts.filter((contract) =>
      contract.wallet ? true : contract.guarantor?.id !== 1,
    );
    const cashPurchase = contracts.filter(
      (item) => item.cash_purchase === true,
    );

    if (qr) {
      if (isCash) {
        const melatCard = otherGuarantors.filter((contract) =>
          contract?.title?.includes("ملت"),
        );
        return [...cashPurchase, ...melatCard];
      } else if (qrFormData?.pType) {
        const creditCards = contracts.filter(
          (contract) =>
            !contract.is_usable &&
            !contract.cash_purchase &&
            contract.card_type !== TAB_KEY_NAGHDI,
        );
        return [...new Set(sortByCreditRemain(creditCards))];
      }
      return [
        ...new Set([
          ...cashPurchase,
          ...sortByCreditRemain(otherGuarantors),
          ...sortByCreditRemain(nasibaGuarantors),
        ]),
      ];
    }

    return [
      ...new Set([
        ...cashPurchase,
        ...sortByCreditRemain(nasibaGuarantors),
        ...sortByCreditRemain(otherGuarantors),
      ]),
    ];
  };

  // Setup contracts when user data changes
  useEffect(() => {
    if (user?.contracts) {
      const contracts = sortContracts(
        qr ? qrContracts : [...user.contracts, ...user.cash_accounts],
      );
      setUserContracts(contracts);
      if (setContract) {
        setContract(contracts[0]);
      }
    }
  }, [user, qrContracts, qr, setContract]);

  // Set arrow visibility based on current route
  useLayoutEffect(() => {
    if (!router.asPath) return;
    setNoRightArrow(router.asPath.includes("/user/wallet"));
  }, [router]);

  // Navigation helpers
  const goToWalletPage = () => router.push("/user/wallet");

  const handleWalletClick = (item) => {
    if (item.type === 4) {
      router.push(`/user/d-wallet/${item.uid}`);
    }
  };

  // Inform parent of sorted index on contracts update
  useEffect(() => {
    if (userContracts?.length && qr) {
      sortedCardsIndexHandler(userContracts);
    }
  }, [userContracts, qr, sortedCardsIndexHandler]);

  // Common style for slide container
  const slideBaseStyle = {
    position: "relative",
    maxHeight: "113px",
    minHeight: "113px",
    height: "113px",
    width: "260px",
    padding: "16px",
    overflow: "hidden",
  };

  // Render a slide with a background image using Next.js's Image component.
  const BackgroundImage = ({ src }) => (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
      }}
    >
      {src && (
        <Image
          src={src}
          alt="background"
          priority
          width={260}
          height={110}
          quality={100}
        />
      )}
    </div>
  );

  const renderWalletSlide = (item, index) => {
    const bgSrc =
      item?.wallet?.wallet_group?.background_image ||
      "/images/panel/pwa/default-line-bg.png";
    return (
      <SwiperSlide
        key={index}
        style={slideBaseStyle}
        onClick={() => handleWalletClick(item)}
      >
        <BackgroundImage src={bgSrc} />
        <div className="d-flex justify-content-between gap-1 flex-column w-100 h-100">
          <div className="d-flex justify-content-between align-items-center">
            {item?.wallet?.wallet_group?.logo ? (
              <Image
                src={item.wallet.wallet_group.logo}
                alt="wallet logo"
                width={32}
                height={32}
              />
            ) : (
              <Image
                src="/images/panel/nasiba_logo.png"
                alt="nasiba logo"
                width={32}
                height={32}
              />
            )}
            {item?.wallet?.id ? (
              <span
                style={{
                  fontSize: "16px",
                  color: item?.wallet?.wallet_group?.front_color,
                }}
              >
                {item?.wallet?.wallet_group?.name}
              </span>
            ) : (
              <span
                style={{
                  fontSize: 15,
                  color: item?.wallet?.wallet_group?.front_color,
                }}
              >
                حداعتباری شما توسط {item?.title} لغو شده
              </span>
            )}
          </div>
          <div
            style={{ marginTop: "12px" }}
            className="d-flex justify-content-between align-items-center w-100"
          >
            {qr === false ? (
              <>
                <p
                  className="w-100 custom-font-size d-flex justify-content-around m-0 mt-3 mb-2"
                  style={{ color: item?.wallet?.wallet_group?.front_color }}
                >
                  <span>سقف حداعتباری:</span>
                  <span>
                    {commafy(Math.trunc(item.withdrawable_amount))} ریال
                  </span>
                </p>
                <p
                  className="w-100 d-flex justify-content-around custom-font-size"
                  style={{ color: item?.wallet?.wallet_group?.front_color }}
                >
                  <span>حداعتباری :</span>
                  <span>
                    {commafy(Math.trunc(item.withdrawable_amount))} ریال
                  </span>
                </p>
              </>
            ) : (
              <div style={{ color: item?.wallet?.wallet_group?.front_color }}>
                <span
                  style={{ fontSize: 14, fontWeight: 500 }}
                  className="mt-1"
                >
                  {item.withdrawable_amount && (
                    <>
                      <span style={{ fontSize: 14, fontWeight: 500 }} dir="rtl">
                        ریال
                      </span>
                      <span
                        style={{ fontSize: 14, fontWeight: 500 }}
                        title={item.withdrawable_amount}
                        className="me-1"
                      >
                        {commafy(Math.trunc(item.withdrawable_amount))}
                      </span>
                    </>
                  )}
                </span>
              </div>
            )}
            <div
              style={{
                fontSize: "14px",
                color: item?.wallet?.wallet_group?.front_color,
              }}
            >
              <p className="m-0" style={{ bottom: "3px", left: "13px" }}>
                موجودی
              </p>
            </div>
          </div>
        </div>
      </SwiperSlide>
    );
  };

  // Render guarantor or credit card slide
  const renderContractSlide = (item, index) => {
    const bgSrc =
      item.guarantor?.background_image ||
      "/images/panel/pwa/default-line-bg.png";
    return (
      <SwiperSlide key={index} style={slideBaseStyle}>
        <BackgroundImage src={bgSrc} />
        <Link className="w-100 h-100" href="/user/trx">
          <div className="d-flex justify-content-between gap-1 flex-column w-100 h-100">
            <div className="d-flex justify-content-between align-items-start w-100">
              {item?.guarantor?.logo || item?.wallet?.logo ? (
                <Image
                  src={item.guarantor?.logo || item?.wallet?.logo}
                  alt="logo"
                  width={32}
                  height={32}
                />
              ) : (
                <Image
                  src="/images/panel/nasiba_logo.png"
                  alt="nasiba logo"
                  width={32}
                  height={32}
                />
              )}
              {item?.is_active || !!item?.wallet?.id ? (
                <span
                  style={{
                    color: item.guarantor?.font_color,
                    fontWeight: 400,
                    fontSize: "16px",
                  }}
                >
                  {item?.guarantor?.name || item?.wallet?.title}
                </span>
              ) : (
                <span
                  className="text-center"
                  style={{ fontSize: 15, color: item.guarantor?.font_color }}
                >
                  حداعتباری شما توسط{" "}
                  {item?.guarantor?.name || item?.wallet?.title} لغو شده
                </span>
              )}
            </div>
            <div className="d-flex justify-content-between align-items-center w-100">
              {qr === false ? (
                <>
                  <p
                    className="w-100 custom-font-size d-flex justify-content-around m-0 mt-3 mb-2"
                    style={{ color: item.guarantor?.font_color }}
                  >
                    <span style={{ color: item.guarantor?.font_color }}>
                      سقف حداعتباری:
                    </span>
                    <span>{commafy(Math.trunc(item.credit_limit))} ریال</span>
                  </p>
                  <p
                    className="w-100 d-flex justify-content-around custom-font-size"
                    style={{ color: item.guarantor?.font_color }}
                  >
                    <span style={{ color: item.guarantor?.font_color }}>
                      حداعتباری :
                    </span>
                    <span
                      style={{
                        color: item.guarantor?.font_color,
                        fontWeight: 500,
                        fontSize: "14px",
                      }}
                    >
                      {commafy(Math.trunc(item.credit_limit_remain))} ریال
                    </span>
                  </p>
                </>
              ) : (
                <span style={{ fontSize: 14 }} className="mt-1">
                  <span
                    style={{
                      color: item.guarantor?.font_color,
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                    dir="rtl"
                  >
                    {" "}
                    ریال
                  </span>
                  <span
                    style={{
                      color: item.guarantor?.font_color,
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                    title={item.credit_limit_remain}
                  >
                    {" "}
                    {commafy(Math.trunc(item.credit_limit_remain))}
                  </span>
                </span>
              )}
              {item?.is_active && (
                <div style={{ fontSize: "14px" }}>
                  <p
                    className="p-0 m-0"
                    style={{
                      color: item.guarantor?.font_color,
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    موجودی
                  </p>
                </div>
              )}
            </div>
          </div>
        </Link>
      </SwiperSlide>
    );
  };

  if (!userContracts?.length) return null;

  return (
    <UserCardContainer>
      <Swiper
        onSwiper={setSwiper}
        ref={swiperRef}
        className="mySwiper"
        spaceBetween={12}
        slidesPerView={1}
        onSlideChange={(s) => {
          setNoRightArrow(false);
          if (setContract) setContract(userContracts[s.activeIndex]);
          if (setActiveSlideHandler) setActiveSlideHandler(s.activeIndex);
        }}
      >
        {userContracts.map((item, index) =>
          item.wallet
            ? renderWalletSlide(item, index)
            : renderContractSlide(item, index),
        )}

        {showWallet && user.main_cash_account && (
          <SwiperSlide
            key="wallet"
            style={{
              ...slideBaseStyle,
              minWidth: "260px",
              background: "transparent",
            }}
            onClick={goToWalletPage}
          >
            <BackgroundImage
              src={
                user.main_cash_account.wallet.wallet_group?.background_image ||
                "#074173"
              }
            />
            <div className="d-flex justify-content-between gap-1 flex-column w-100 h-100">
              <Link className="w-100 h-100" href="/user/wallet">
                <div
                  className="w-100 d-flex align-items-center justify-content-between flex-column h-100"
                  style={{ fontSize: 16 }}
                >
                  <div className="d-flex justify-content-between align-items-center w-100">
                    {user.main_cash_account.wallet.wallet_group?.logo ? (
                      <Image
                        src={user.main_cash_account.wallet.wallet_group.logo}
                        alt="wallet logo"
                        width={32}
                        height={32}
                      />
                    ) : (
                      <Image
                        src="/icons/wallet.svg"
                        alt="wallet icon"
                        width={32}
                        height={32}
                      />
                    )}
                    <span
                      style={{
                        color:
                          user.main_cash_account.wallet.wallet_group
                            ?.font_color,
                        fontWeight: 400,
                        fontSize: "16px",
                      }}
                    >
                      کیف پول
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div
                      className="amount mt-1 d-flex gap-1"
                      style={{
                        color:
                          user.main_cash_account.wallet.wallet_group
                            ?.font_color,
                        fontWeight: 500,
                        fontSize: "14px",
                      }}
                    >
                      <span>
                        {commafy(
                          +user.main_cash_account.withdrawable_amount || 0,
                        )}
                      </span>
                      <span>ریال</span>
                    </div>
                    <span
                      style={{
                        color:
                          user.main_cash_account.wallet.wallet_group
                            ?.font_color,
                        fontWeight: 500,
                        fontSize: "14px",
                      }}
                    >
                      موجودی
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </SwiperSlide>
        )}
      </Swiper>
    </UserCardContainer>
  );
};
